import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Select, Table } from 'antd';
import { InfiniteScroll, SearchInput, Spin } from 'common/components';
import { useInventoryContext } from 'common/context';
import {
  DEFAULT_EMPTY_VALUE,
  DEFAULT_LIST_OFFSET,
  DEFAULT_PAGINATION_PAGE,
  LIST_LIMIT_20,
  SELECT_LIST_HEIGHT_320,
} from 'common/config';
import { IOption } from 'common/models';
import { UserHooks } from 'common/hooks';
import { ArrowDownShort } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { InventoryCard, InventoryEmptyList } from 'entities/Inventory/components';
import { EInventoryStatus } from 'entities/Inventory/Inventory.enum';
import { IInventoryListPayload } from 'entities/Inventory/Inventory.models';
import { renderInventoryListRecords } from 'entities/Inventory/Inventory.helper';
import { IUserInventoryStatsParams } from 'entities/User/User.models';
import {
  getInventoryById,
  getInventoryList,
  getInventoryListChunk,
  getInventoryListSubdivisions,
  getInventoryListUsers,
  selectInventoryCount,
  selectInventoryList,
  selectIsInventoryLoading,
  setInventoryList,
} from 'entities/Inventory/Inventory.slice';
import { getUserInventoryStats, selectIsUserLoading } from 'entities/User/User.slice';
import { selectIsGoodLoading } from 'entities/Goods/Good.slice';

interface IComponentProps {
  header: ReactNode;
  menu: ReactNode;
  subdivisionOptions: IOption[];
  userOptions: IOption[];
}

export const WriteOffInventoryListTab: FC<IComponentProps> = ({ header, menu, subdivisionOptions, userOptions }) => {
  const dispatch = useAppDispatch();
  // good
  const isGoodLoading = useAppSelector(selectIsGoodLoading);
  // inventory
  const inventoryList = useAppSelector(selectInventoryList);
  const inventoryCount = useAppSelector(selectInventoryCount);
  const isInventoryLoading = useAppSelector(selectIsInventoryLoading);
  // user
  const isUserLoading = useAppSelector(selectIsUserLoading);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [scrollLoading, setScrollLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_PAGINATION_PAGE);
  const [payload, setPayload] = useState<IInventoryListPayload>({
    statuses: [EInventoryStatus.WriteOff],
    limit: LIST_LIMIT_20,
    offset: DEFAULT_LIST_OFFSET,
  });
  const { setOpenInventoryCard } = useInventoryContext();
  const { isAccountAdmin, isHeadOfDepartment } = UserHooks.useCurrentUserRole();

  const userInventoryStatsParams: IUserInventoryStatsParams = {
    myStats: isAccountAdmin || isHeadOfDepartment ? undefined : true,
  };

  const onSubdivisionChange = async (value: number) => {
    const newPayload = {
      ...payload,
      subdivisionIds: value !== DEFAULT_EMPTY_VALUE ? [value] : undefined,
      userId: undefined,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);

    await dispatch(
      getInventoryListUsers({ statuses: [EInventoryStatus.WriteOff], subdivisionId: newPayload.subdivisionIds?.[0] }),
    );
    await dispatch(getInventoryList(newPayload));
  };

  const onUserChange = (value: number) => {
    const newPayload = { ...payload, userId: value === DEFAULT_EMPTY_VALUE ? undefined : value };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);
    dispatch(getInventoryList(newPayload));
  };

  const onSearchClick = async (value?: string) => {
    const newPayload = { ...payload, search: value };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);
    await dispatch(getInventoryList(newPayload));
  };

  useEffect(() => {
    const fetch = async () => {
      dispatch(setInventoryList([]));
      await dispatch(getUserInventoryStats(userInventoryStatsParams));

      if (isAccountAdmin) {
        await dispatch(getInventoryListSubdivisions({ statuses: [EInventoryStatus.WriteOff] }));
        await dispatch(getInventoryListUsers({ statuses: [EInventoryStatus.WriteOff] }));
      }

      await dispatch(getInventoryList(payload));
      setFetchLoading(false);
    };

    fetch();
  }, []);

  return (
    <>
      <InfiniteScroll
        loading={scrollLoading}
        hasMore={!isInventoryLoading && inventoryList.length < inventoryCount}
        loader={<Spin />}
        scrollToTopTrigger={[payload]}
        loadMore={async () => {
          setScrollLoading(true);
          setPage(page + 1);
          await dispatch(
            getInventoryListChunk({
              ...payload,
              offset: LIST_LIMIT_20 * page,
            }),
          );
          setScrollLoading(false);
        }}
      >
        {header}
        {menu}

        <div className="inventory-list__filters flex mb-12">
          {isAccountAdmin && (
            <>
              <Select
                rootClassName="redesign"
                className="inventory-list__filters-select"
                options={subdivisionOptions}
                value={payload?.subdivisionIds ? payload.subdivisionIds[0] : DEFAULT_EMPTY_VALUE}
                onChange={onSubdivisionChange}
                placeholder="Выберите компанию"
                listHeight={SELECT_LIST_HEIGHT_320}
                virtual={false}
                suffixIcon={<ArrowDownShort />}
              />

              <Select
                rootClassName="redesign"
                className="inventory-list__filters-select"
                options={userOptions}
                value={payload?.userId !== undefined ? payload.userId : DEFAULT_EMPTY_VALUE}
                onChange={onUserChange}
                placeholder="Выберите пользователя"
                listHeight={SELECT_LIST_HEIGHT_320}
                virtual={false}
                suffixIcon={<ArrowDownShort />}
              />
            </>
          )}

          <SearchInput placeholder="Искать по названию или артикулу" onSearch={onSearchClick} />
        </div>

        <Table
          className={`
                table-hover
                ${!!inventoryList.length && inventoryList.length === inventoryCount && 'table-end-of-list'}
                ${!inventoryList.length && 'table-empty'}
              `}
          dataSource={inventoryList.map((inventory) => ({ ...inventory, key: inventory.id }))}
          columns={renderInventoryListRecords()}
          onRow={({ id }) => ({ onClick: () => dispatch(getInventoryById({ id, onSuccess: () => setOpenInventoryCard(true) })) })}
          showHeader={false}
          pagination={false}
          loading={isInventoryLoading || isGoodLoading || isUserLoading}
          locale={{
            emptyText: fetchLoading ? <Spin /> : <InventoryEmptyList title="Оборудование в подразделении отсутствует" />,
          }}
        />
      </InfiniteScroll>

      <InventoryCard />
    </>
  );
};
